import Cookies from 'js-cookie';
import { Action, DecisionResponse } from './decisionResponse';
import { MonetateRequest } from './eventRequest';
import { log } from '../../utils/logger';
import { logAPIErrorToNewrelic } from '../wishList/utils';
import { MonetateExperience, recordImpressionByName } from '@JohnLewisPartnership/experiments-utils';

const prodMonetateChannel = 'a-76ca7dd3/p/johnlewis.com';
const devMonetateChannel = 'a-76ca7dd3/d/dev-johnlewis.com';

const baseMonetateConfig = {
    url: 'https://engine.monetate.net/api/engine/v1/decide/JohnLewis',
    cookieKey: 'mt.v',
};

export const getMonetateConfig = (): MonetateConfig => {
    const channel = process.env.REACT_APP_ENV === 'prod' ? prodMonetateChannel : devMonetateChannel;
    return { ...baseMonetateConfig, channel };
};

export const getMonetateActions = async (): Promise<Action[]> => {
    const monetateConfig = getMonetateConfig();

    try {
        const monetateDecisionRequest = buildMonetateRequest(monetateConfig, 'monetate:decision:DecisionRequest');

        const resp = await fetch(monetateConfig.url, {
            method: 'POST',
            body: JSON.stringify(monetateDecisionRequest),
        });

        if (!resp.ok) {
            logAPIErrorToNewrelic('getMonetateActions', 'Monetate | Response Not OK | Error thrown', {
                ...resp,
                errorThrown: true,
                response: resp,
            });
            throw new Error(`Status code ${resp.status}`);
        }

        const decisionResponse: DecisionResponse = await resp.json();
        const responses = decisionResponse.data.responses[0];

        return responses === undefined ? [] : responses.actions;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
        logAPIErrorToNewrelic('getMonetateActions', 'Monetate | Exception', { exception: e });
        console.error('Error while getting Monetate actions:', e.message ?? e);
        return [];
    }
};

export const buildMonetateRequest = (
    monetateConfig: MonetateConfig,
    eventType: string,
    impressionIds?: string[],
    requestId: string = createRandomNumber(),
): MonetateRequest => {
    const monetateId = Cookies.get(monetateConfig.cookieKey);

    if (!monetateId) {
        logAPIErrorToNewrelic('buildMonetateRequest', 'Monetate | Monetate Id Not Present | Error thrown', {
            errorThrown: true,
        });
        throw new Error(`Monetate cookie '${monetateConfig.cookieKey}' not found or blank`);
    }

    return {
        channel: monetateConfig.channel,
        monetateId: monetateId,
        events: [
            {
                eventType,
                requestId: requestId,
                impressionIds,
            },
        ],
    };
};

export const shouldCollapseWishLists = async (): Promise<boolean> => {
    const actions = await getMonetateActions();

    const collapsedWishListAction = actions.find((action) => action.json?.doCollapsed === true);

    if (!collapsedWishListAction) return false;

    if (!collapsedWishListAction.impressionId) {
        console.error('Monetate impressionId not found on action');
        return false;
    }

    log('Found collapse wish list action:', collapsedWishListAction);

    postMonetateImpression(collapsedWishListAction.impressionId);

    return collapsedWishListAction.isControl === false;
};

export const postMonetateImpression = async (
    impressionId: string,
    requestId: string = createRandomNumber(),
): Promise<boolean> => {
    const monetateConfig = getMonetateConfig();

    try {
        const impressionRequest = buildMonetateRequest(
            monetateConfig,
            'monetate:record:Impressions',
            [impressionId],
            requestId,
        );

        const resp = await fetch(monetateConfig.url, {
            method: 'POST',
            body: JSON.stringify(impressionRequest),
        });

        if (!resp.ok) {
            logAPIErrorToNewrelic('postMonetateImpression', 'Monetate | Response Not OK | Error thrown', {
                ...resp,
                errorThrown: true,
                response: resp,
            });
            throw new Error(`Response status ${resp.status}`);
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
        console.error('Error while sending Monetate impression:', e.message ?? e);
        logAPIErrorToNewrelic('postMonetateImpression', 'Monetate | Exception', { exception: e });
        return false;
    }

    return true;
};

export const trackEvent = async (eventKey: string): Promise<void> => {
    window.monetateQ = window.monetateQ || [];
    const monetateQItem = ['trackEvent', [eventKey]];

    log('Pushing onto monetateQ:', monetateQItem);
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.monetateQ.push(monetateQItem);
};

interface MonetateConfig {
    channel: string;
    url: string;
    cookieKey: string;
}

function createRandomNumber(): string {
    return Math.floor(Math.random() * 10000000000).toString();
}

export const postMonetateImpressionByName = (name: string, experiences: MonetateExperience[]): void => {
    const monetateConfig = getMonetateConfig();
    const { channel, cookieKey } = monetateConfig;
    const monetateId = Cookies.get(cookieKey) || '';

    recordImpressionByName(channel, experiences, name, monetateId);
};
